import { type RefObject } from 'react';
import type { ChatMessage, ComposerTab, DiagramRevision, PendingImage, PipelineStage } from '../../store/types.ts';
import { PipelineStepper } from './PipelineStepper.tsx';
import { ChatView } from './ChatView.tsx';
import { CodeEditor } from './CodeEditor.tsx';
import { RevisionHistory } from './RevisionHistory.tsx';

type PromptPanelProps = {
  activeTab: ComposerTab;
  onTabChange: (tab: ComposerTab) => void;
  // Chat props
  chat: ChatMessage[];
  isSending: boolean;
  pipelineStage: PipelineStage;
  pendingImage: PendingImage | null;
  promptText: string;
  hasLastRequest: boolean;
  onPromptChange: (text: string) => void;
  onSend: () => void;
  onRetry: () => void;
  onClearChat: () => void;
  onStop: () => void;
  onAutoFix: () => void;
  onAttachImage: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveImage: () => void;
  promptRef: RefObject<HTMLTextAreaElement | null>;
  // Code props
  editorCode: string;
  onEditorChange: (code: string) => void;
  onRender: () => void;
  onCopy: () => void;
  onExportMmd: () => void;
  editorRef: RefObject<HTMLTextAreaElement | null>;
  // Revision props
  revisions: Array<DiagramRevision & { index: number }>;
  currentCursor: number;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onRestore: (index: number) => void;
  // Diagram type selector
  onDiagramTypePrefix: (prefix: string) => void;
};

export function PromptPanel(props: PromptPanelProps) {
  return (
    <div className="ds-prompt-panel">
      <div className="ds-tabs" role="tablist" aria-label="Prompt panel tabs">
        {(['chat', 'code', 'history'] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={props.activeTab === tab}
            className={`ds-tab ${props.activeTab === tab ? 'ds-tab--active' : ''}`}
            onClick={() => props.onTabChange(tab)}
          >
            {tab === 'chat' ? 'Chat' : tab === 'code' ? 'Code' : 'History'}
          </button>
        ))}
      </div>

      <PipelineStepper pipelineStage={props.pipelineStage} />

      {props.activeTab === 'chat' && (
        <ChatView
          chat={props.chat}
          isSending={props.isSending}
          pipelineStage={props.pipelineStage}
          pendingImage={props.pendingImage}
          promptText={props.promptText}
          hasLastRequest={props.hasLastRequest}
          onPromptChange={props.onPromptChange}
          onSend={props.onSend}
          onRetry={props.onRetry}
          onClearChat={props.onClearChat}
          onStop={props.onStop}
          onAutoFix={props.onAutoFix}
          onAttachImage={props.onAttachImage}
          onRemoveImage={props.onRemoveImage}
          promptRef={props.promptRef}
          onDiagramTypePrefix={props.onDiagramTypePrefix}
        />
      )}

      {props.activeTab === 'code' && (
        <CodeEditor
          editorCode={props.editorCode}
          onChange={props.onEditorChange}
          onRender={props.onRender}
          onCopy={props.onCopy}
          onExportMmd={props.onExportMmd}
          editorRef={props.editorRef}
        />
      )}

      {props.activeTab === 'history' && (
        <RevisionHistory
          revisions={props.revisions}
          currentCursor={props.currentCursor}
          canUndo={props.canUndo}
          canRedo={props.canRedo}
          onUndo={props.onUndo}
          onRedo={props.onRedo}
          onRestore={props.onRestore}
        />
      )}
    </div>
  );
}
